import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import type { FeedbackCampaign, FeedbackLocation } from '../../../types/dynamic-feedback';
import { feedbackCampaignsService } from '../services/feedbackCampaignsService';
import { QuestionBuilder } from './QuestionBuilder';

interface CampaignEditorModalProps {
  campaign: FeedbackCampaign | null;
  onClose: () => void;
  onSaved: () => void;
}

export const CampaignEditorModal = ({ campaign, onClose, onSaved }: CampaignEditorModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'active' | 'draft'>('draft');
  const [targetLocations, setTargetLocations] = useState<FeedbackLocation[]>([]);
  const [questions, setQuestions] = useState(campaign?.questions || []);
  const [maxShows, setMaxShows] = useState<number | ''>('');
  const [minCooldown, setMinCooldown] = useState<number | ''>('');
  const [maxShowCooldown, setMaxShowCooldown] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allLocations: FeedbackLocation[] = [
    'Lesson',
    'Tool',
    'TriggerLog',
    'Chat',
    'ObsessionFlow',
    'PanicAttackFlow',
    'CompulsionFlow',
    'ChatQuest',
    'Insights',
  ];

  useEffect(() => {
    if (campaign) {
      setName(campaign.name);
      setDescription(campaign.description);
      setStatus(campaign.status);
      setTargetLocations(campaign.targetLocations);
      setQuestions(campaign.questions);
      setMaxShows(campaign.cooldownConfig?.maxShows ?? '');
      setMinCooldown(campaign.cooldownConfig?.minCooldown ?? '');
      setMaxShowCooldown(campaign.cooldownConfig?.maxShowCooldown ?? '');
    }
  }, [campaign]);

  const handleLocationToggle = (location: FeedbackLocation) => {
    if (targetLocations.includes(location)) {
      setTargetLocations(targetLocations.filter((l) => l !== location));
    } else {
      setTargetLocations([...targetLocations, location]);
    }
  };

  const handleSave = async () => {
    setError(null);

    // Validation
    if (!name.trim()) {
      setError('Campaign name is required');
      return;
    }

    if (targetLocations.length === 0) {
      setError('At least one location must be selected');
      return;
    }

    if (questions.length === 0) {
      setError('At least one question is required');
      return;
    }

    setSaving(true);

    try {
      // Build cooldown config
      const cooldownConfig =
        maxShows !== '' || minCooldown !== '' || maxShowCooldown !== ''
          ? {
              ...(maxShows !== '' && { maxShows: Number(maxShows) }),
              ...(minCooldown !== '' && { minCooldown: Number(minCooldown) }),
              ...(maxShowCooldown !== '' && { maxShowCooldown: Number(maxShowCooldown) }),
            }
          : undefined;

      if (campaign) {
        // Update existing
        await feedbackCampaignsService.updateCampaign(campaign.id, {
          name,
          description,
          status,
          targetLocations,
          questions,
          cooldownConfig,
        });
      } else {
        // Create new
        await feedbackCampaignsService.createCampaign({
          name,
          description,
          status,
          targetLocations,
          questions,
          cooldownConfig,
        });
      }

      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={campaign ? 'Edit Campaign' : 'New Campaign'} size="xl">
      <div className="space-y-6">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Post-Obsession Flow Feedback"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Brief description of this campaign's purpose"
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
          </select>
        </div>

        {/* Target Locations */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Locations ({targetLocations.length} selected)
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {allLocations.map((location) => (
              <label
                key={location}
                className="flex items-center gap-2 px-3 py-2 border rounded cursor-pointer hover:bg-gray-50 min-w-0"
              >
                <input
                  type="checkbox"
                  checked={targetLocations.includes(location)}
                  onChange={() => handleLocationToggle(location)}
                  className="rounded"
                />
                <span className="text-sm min-w-0 break-words">{location}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Cooldown Configuration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cooldown Configuration
          </label>
          <p className="text-xs text-gray-600 mb-3">
            Control how often users see this campaign. Leave empty for no limit.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Min Cooldown (hours)</label>
              <input
                type="number"
                value={minCooldown}
                onChange={(e) =>
                  setMinCooldown(e.target.value === '' ? '' : Number(e.target.value))
                }
                min="0"
                step="0.5"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum time between shows (always enforced)
              </p>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max Shows</label>
              <input
                type="number"
                value={maxShows}
                onChange={(e) => setMaxShows(e.target.value === '' ? '' : Number(e.target.value))}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum times to show before long cooldown
              </p>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Max Show Cooldown (hours)
              </label>
              <input
                type="number"
                value={maxShowCooldown}
                onChange={(e) =>
                  setMaxShowCooldown(e.target.value === '' ? '' : Number(e.target.value))
                }
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 24"
              />
              <p className="text-xs text-gray-500 mt-1">
                Hours to wait after reaching max shows
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2 italic">
            💡 Example: maxShows=2, minCooldown=1, maxShowCooldown=24 → User sees it twice (1hr
            apart), then waits 24hrs
          </p>
        </div>

        {/* Question Builder */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Questions ({questions.length})
          </label>
          <QuestionBuilder questions={questions} onChange={setQuestions} />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button onClick={onClose} variant="ghost" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : campaign ? 'Save Changes' : 'Create Campaign'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
